import * as store from "../../lib/store.js";

/**
 * The device credential, in whichever shape this device happens to hold it.
 *
 * [V] **An upgraded device would have failed at everything, silently.** The client this
 * replaces persisted `{token, claimProfileId, participantId, displayName}` to IndexedDB
 * (`54d6ced:web/app.js:692`) and read `credentials.claimProfileId` everywhere
 * (`54d6ced:web/app.js:1570`). The port reads `credentials.claim_profile_id`. Fresh enrolments
 * write both keys, so every test and every QA run passed -- and every device that had
 * already enrolled would have read `undefined`:
 *
 * - `isCreator` compares `creator_claim_profile_id` against `undefined`, so it is always false:
 *   the person who started the session presses "End the session for everyone", the client
 *   calls `leaveSession` instead, and the session is never finalised for the table.
 * - the signed payload carries `claim_profile_id: undefined`, which serialises away, so the
 *   server answers 422 and the outbox -- correctly -- drops every bundle as unfixable.
 *   100% of that device's claims, with an error on screen every 20 seconds.
 * - joining fails, and Diagnostics reports the device as "—".
 *
 * Nothing about that is loud enough to notice before a session is already lost, which is
 * why the reconciliation happens once, at boot, and is written back.
 */
export interface StoredCredentials {
  token: string;
  claim_profile_id: string;
  claimProfileId: string;
  participantId: string;
  displayName: string;
  [key: string]: unknown;
}

/** What `loadCredentials` can return: either shape, or a partial one. */
type RawCredentials = Record<string, unknown> | null | undefined;

const str = (value: unknown): string | undefined =>
  typeof value === "string" && value.length > 0 ? value : undefined;

/**
 * Both spellings of every field, from whichever one this device stored.
 *
 * Returns `null` for a device that has never enrolled, and `changed: true` when the stored
 * object was missing a key the app now reads -- which is the signal to write it back, so
 * the reconciliation is paid once rather than on every boot.
 *
 * Deliberately not a migration with a version number. There is one legacy shape, it is a
 * strict subset, and both spellings are cheap to keep: a device that downgrades to a cached
 * older bundle mid-deploy still finds the key it expects.
 */
export function normaliseCredentials(raw: RawCredentials): {
  credentials: StoredCredentials | null;
  changed: boolean;
} {
  if (!raw || typeof raw !== "object") return { credentials: null, changed: false };

  const token = str(raw.token);
  const claimProfileId = str(raw.claim_profile_id) ?? str(raw.claimProfileId);
  // A credential with no token or no device id is not a credential. Treated as absent
  // rather than repaired: the device re-enrols, which is the only thing that can produce a
  // usable one, and pretending otherwise would put an unusable identity into every claim.
  if (!token || !claimProfileId) return { credentials: null, changed: false };

  const participantId = str(raw.participant_id) ?? str(raw.participantId) ?? "";
  const displayName = str(raw.display_name) ?? str(raw.displayName) ?? "";

  const credentials: StoredCredentials = {
    ...raw,
    token,
    claim_profile_id: claimProfileId,
    claimProfileId,
    participantId,
    displayName,
  };

  const changed = (["token", "claim_profile_id", "claimProfileId", "participantId", "displayName"] as const).some(
    (key) => raw[key] !== credentials[key]
  );
  return { credentials, changed };
}

/**
 * Normalise what IndexedDB holds, and heal it in place when it was the old shape.
 *
 * The write-back is not an optimisation. `store.saveCredentials` is the only thing that
 * makes the next boot cheap *and* makes a device that downgrades to a cached older bundle
 * -- which reads `claimProfileId` -- keep working, because by then the record carries both.
 */
export async function loadNormalisedCredentials(
  load: () => Promise<RawCredentials> = store.loadCredentials,
  save: (credentials: StoredCredentials) => Promise<void> = store.saveCredentials
): Promise<StoredCredentials | null> {
  const { credentials, changed } = normaliseCredentials(await load());
  if (credentials && changed) {
    // Failing to write it back is not failing to boot: the in-memory copy is already
    // correct and this run works either way.
    await save(credentials).catch(() => {});
  }
  return credentials;
}
